/**
 * Payment Display Utilities
 *
 * Utility functions to ensure consistent payment method and status display
 * across all components in the application.
 */

import {
  FaCreditCard,
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle
} from 'react-icons/fa';

/**
 * Get standardized payment method display text with icon
 * @param {string} paymentMethod - The payment method from booking data
 * @returns {JSX.Element} Display element with icon
 */
export const getPaymentMethodDisplay = (paymentMethod) => {
  if (!paymentMethod || typeof paymentMethod !== "string") {
    return (
      <>
        <FaCreditCard className="inline mr-2" />
        Razorpay
      </>
    );
  }

  const method = paymentMethod.toLowerCase();

  if (method === "cod" || method === "cash" || method === "cash on delivery") {
    return (
      <>
        <FaMoneyBillWave className="inline mr-2" />
        Cash Payment
      </>
    );
  }

  // For all online payment methods (ONLINE, online, razorpay, card, upi, etc.)
  return (
    <>
      <FaCreditCard className="inline mr-2" />
      Razorpay
    </>
  );
};

/**
 * Get standardized payment status display text with icon
 * @param {Object} booking - The booking object
 * @returns {JSX.Element} Display element with icon
 */
export const getPaymentStatusDisplay = (booking) => {
  if (!booking || typeof booking !== "object") {
    return (
      <>
        <FaClock className="inline mr-2" />
        Pending
      </>
    );
  }

  const { paymentMethod, status, paymentStatus } = booking;

  // For COD payments - check if ride is completed OR payment status is completed
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    (paymentMethod.toLowerCase() === "cod" ||
      paymentMethod.toLowerCase() === "cash" ||
      paymentMethod.toLowerCase() === "cash on delivery")
  ) {
    // If payment status is explicitly "completed" OR ride is completed, show as Paid
    if (
      (paymentStatus && paymentStatus.toLowerCase() === "completed") ||
      (status && (status.toLowerCase() === "completed" || status.toLowerCase() === "finished"))
    ) {
      return (
        <>
          <FaCheckCircle className="inline mr-2 text-green-600" />
          <span className="text-green-600">Paid</span>
        </>
      );
    }
    // Otherwise show as Pending
    return (
      <>
        <FaClock className="inline mr-2" />
        Pending
      </>
    );
  }

  // For all online payments - show only "Paid" without additional text
  return (
    <>
      <FaCheckCircle className="inline mr-2 text-green-600" />
      <span className="text-green-600">Paid</span>
    </>
  );
};

/**
 * Check if payment method is online/digital
 * @param {string} paymentMethod - The payment method
 * @returns {boolean} True if online payment
 */
export const isOnlinePayment = (paymentMethod) => {
  if (!paymentMethod || typeof paymentMethod !== "string") return true; // Default to online

  const method = paymentMethod.toLowerCase();
  return !(
    method === "cod" ||
    method === "cash" ||
    method === "cash on delivery"
  );
};

/**
 * Get payment method badge color class
 * @param {string} paymentMethod - The payment method
 * @returns {string} CSS class for styling
 */
export const getPaymentMethodBadgeClass = (paymentMethod) => {
  if (isOnlinePayment(paymentMethod)) {
    return "bg-blue-100 text-blue-800";
  }
  return "bg-yellow-100 text-yellow-800";
};

/**
 * Get payment status badge color class
 * @param {Object} booking - The booking object
 * @returns {string} CSS class for styling
 */
export const getPaymentStatusBadgeClass = (booking) => {
  if (!booking || typeof booking !== "object") {
    return "bg-yellow-100 text-yellow-800"; // Default to pending
  }

  const { paymentMethod, status, paymentStatus } = booking;

  // For COD payments - check if ride is completed OR payment status is completed
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    (paymentMethod.toLowerCase() === "cod" ||
      paymentMethod.toLowerCase() === "cash" ||
      paymentMethod.toLowerCase() === "cash on delivery")
  ) {
    // If payment status is explicitly "completed" OR ride is completed, show green (Paid)
    if (
      (paymentStatus && paymentStatus.toLowerCase() === "completed") ||
      (status && (status.toLowerCase() === "completed" || status.toLowerCase() === "finished"))
    ) {
      return "bg-green-100 text-green-800";
    }
    // Otherwise show yellow (Pending)
    return "bg-yellow-100 text-yellow-800";
  }

  // For all online payments - show as Paid (green)
  return "bg-green-100 text-green-800";
};

/**
 * Get payment method icon component
 * @param {string} paymentMethod - The payment method
 * @returns {JSX.Element} React icon component
 */
export const getPaymentMethodIcon = (paymentMethod) => {
  if (isOnlinePayment(paymentMethod)) {
    return <FaCreditCard className="inline mr-2" />;
  }
  return <FaMoneyBillWave className="inline mr-2" />;
};

/**
 * Get payment status icon component
 * @param {Object} booking - The booking object
 * @returns {JSX.Element} React icon component
 */
export const getPaymentStatusIcon = (booking) => {
  if (!booking || typeof booking !== "object") {
    return <FaClock className="inline mr-2" />; // Default to pending
  }

  const { paymentMethod, status, paymentStatus } = booking;

  // For COD payments - check if ride is completed OR payment status is completed
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    (paymentMethod.toLowerCase() === "cod" ||
      paymentMethod.toLowerCase() === "cash" ||
      paymentMethod.toLowerCase() === "cash on delivery")
  ) {
    // If payment status is explicitly "completed" OR ride is completed, show checkmark
    if (
      (paymentStatus && paymentStatus.toLowerCase() === "completed") ||
      (status && (status.toLowerCase() === "completed" || status.toLowerCase() === "finished"))
    ) {
      return <FaCheckCircle className="inline mr-2" />;
    }
    // Otherwise show clock (Pending)
    return <FaClock className="inline mr-2" />;
  }

  // For all online payments - show as Paid
  return <FaCheckCircle className="inline mr-2" />;
};
